/**
 * Media API — tenant-scoped capture media (specs/changes/photo-capture).
 * Presigned upload for captured images and authenticated media resolution.
 * The S3 key is derived server-side from the auth token; the client only
 * supplies the content type.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface UploadTarget {
  mediaId: string;
  uploadUrl: string;
  key: string;
  maxBytes: number;
  expiresIn: number;
}

export interface MediaResolution {
  mediaId: string;
  url: string;
  contentType: string;
  expiresIn: number;
}

export const mediaApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** Mint a presigned PUT for a capture image. */
  async getUploadUrl(contentType: string): Promise<UploadTarget> {
    const { data } = await api.post('/media/upload-url', { contentType });
    return data;
  },

  /** Resolve an owned media id to a short-lived download URL. */
  async getMedia(mediaId: string): Promise<MediaResolution> {
    const { data } = await api.get(`/media/${mediaId}`);
    return data;
  },
};

/**
 * PUT a blob to a presigned URL with upload progress (XHR — fetch has no
 * upload progress events). Content type must match the presigned one.
 */
export function uploadToPresignedUrl(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed (network)'));
    xhr.send(blob);
  });
}
